import { z } from 'zod';
import {
    LegalDocumentAssignmentScope,
    LegalDocumentPurpose,
    Status,
} from '@pillar-portal/database/dist/frontend';
import type {
    ApplicableTermsResult,
    AcceptApplicableTermsRequest,
    AcceptOrganizationAgreementRequest,
    CreateLegalDocumentAssignmentRequest,
    LegalDocumentsListRequest,
    LegalDocumentsSearchRequest,
    SearchLegalDocumentAssignmentsPageResponse,
    OrganizationAgreementStatus,
    SearchOrganizationAgreementsPageResponse,
    UpdateLegalDocumentStatusRequest,
    UpdateLegalDocumentRequest,
    RevokeAllLegalDocumentAssignmentsRequest,
    RevokeAllLegalDocumentAssignmentsResponse,
    RevokeLegalDocumentAssignmentRequest,
    LegalDocumentAcceptanceListItem,
    LegalDocumentAcceptanceWithdrawal,
    SearchLegalDocumentAcceptancesPageResponse,
    GetLegalDocumentAcceptanceResponse,
} from '../legal-documents.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { AuthorizationPaginationLimitQuerySchema } from './authorization.schemas';
import { NonEmptyStringSchema } from './common.schemas';
import { SearchTermsSchema } from './omnisearch.schemas';
import { SystemDisplayTimeZoneSchema } from '../timezone.utils';

export const LegalDocumentUuidSchema: z.ZodType<string> = z.string().uuid();

export const LegalDocumentUuidParamSchema: z.ZodType<string> = LegalDocumentUuidSchema;

export const LegalDocumentsOpaqueCursorQuerySchema: z.ZodType<string | undefined> = z
    .string()
    .trim()
    .min(1)
    .optional();

export const LegalDocumentsSearchRequestSchema: z.ZodType<LegalDocumentsSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: LegalDocumentsOpaqueCursorQuerySchema,
    })
    .strict();

export const LegalDocumentsListRequestSchema: z.ZodType<LegalDocumentsListRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: LegalDocumentsOpaqueCursorQuerySchema,
        include_deleted: z.boolean().optional(),
    })
    .strict();

const legalDocumentListItemSchemaObject = z
    .object({
        legal_document_uuid: LegalDocumentUuidSchema,
        legal_document_title: NonEmptyStringSchema,
        legal_document_version: NonEmptyStringSchema,
        status: z.enum(Status),
        legal_document_content_type: NonEmptyStringSchema,
        in_force_start: z.string().datetime(),
        in_force_end: z.string().datetime().nullable(),
        legal_document_purpose: z.enum(LegalDocumentPurpose),
        acceptance_validity_duration_days: z.number().int().positive().nullable(),
        content_sha256: NonEmptyStringSchema,
        source_filename: z.string().optional(),
        content_length_bytes: z.number().int().nonnegative(),
        created_ts: z.string().datetime(),
        updated_ts: z.string().datetime().nullable(),
    })
    .strict()
    .superRefine((legalDocument, context) => {
        if (
            legalDocument.legal_document_purpose !== LegalDocumentPurpose.ORGANIZATION_AGREEMENT &&
            legalDocument.acceptance_validity_duration_days !== null
        ) {
            context.addIssue({
                code: 'custom',
                path: ['acceptance_validity_duration_days'],
                message: 'acceptance validity is only available for Organization agreements',
            });
        }
    });
export const LegalDocumentListItemSchema: typeof legalDocumentListItemSchemaObject =
    legalDocumentListItemSchemaObject;

const searchLegalDocumentsPageResponseSchemaObject = z
    .object({
        legal_documents: z.array(LegalDocumentListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
        display_timezone: SystemDisplayTimeZoneSchema,
    })
    .strict();
export const SearchLegalDocumentsPageResponseSchema: typeof searchLegalDocumentsPageResponseSchemaObject =
    searchLegalDocumentsPageResponseSchemaObject;

const createLegalDocumentRequestSchemaObject = z
    .object({
        legal_document_title: NonEmptyStringSchema,
        legal_document_version: NonEmptyStringSchema,
        legal_document_text: NonEmptyStringSchema,
        legal_document_content_type: NonEmptyStringSchema,
        in_force_start: z.string().datetime(),
        in_force_end: z.string().datetime().nullable().optional(),
        legal_document_purpose: z.enum(LegalDocumentPurpose),
        acceptance_validity_duration_days: z.number().int().positive().nullable().optional(),
        source_filename: z.string().optional(),
    })
    .strict()
    .superRefine((legalDocument, context) => {
        const validityDurationDays = legalDocument.acceptance_validity_duration_days ?? null;
        if (
            legalDocument.legal_document_purpose !== LegalDocumentPurpose.ORGANIZATION_AGREEMENT &&
            validityDurationDays !== null
        ) {
            context.addIssue({
                code: 'custom',
                path: ['acceptance_validity_duration_days'],
                message: 'acceptance validity is only available for Organization agreements',
            });
        }
    });
export const CreateLegalDocumentRequestSchema: typeof createLegalDocumentRequestSchemaObject =
    createLegalDocumentRequestSchemaObject;

const legalDocumentAssignmentPublicBaseSchema = z.object({
    legal_document_assignment_uuid: z.string().uuid(),
    legal_document: LegalDocumentListItemSchema,
    status: z.enum(Status),
    in_force_start: z.string().datetime(),
    in_force_end: z.string().datetime().nullable(),
    created_ts: z.string().datetime(),
    assigned_by_user_uuid: z.string().uuid(),
    revocation: z
        .object({
            revoked_ts: z.string().datetime(),
            revoked_by_user_uuid: z.string().uuid(),
            revoke_reason: z.string().nullable(),
        })
        .strict()
        .nullable(),
});

export const LegalDocumentAssignmentPublicSchema = z.discriminatedUnion('scope', [
    legalDocumentAssignmentPublicBaseSchema
        .extend({
            scope: z.literal(LegalDocumentAssignmentScope.DEFAULT),
            organization: z.null(),
        })
        .strict(),
    legalDocumentAssignmentPublicBaseSchema
        .extend({
            scope: z.literal(LegalDocumentAssignmentScope.ORGANIZATION),
            organization: z
                .object({
                    organization_uuid: z.string().uuid(),
                    organization_name: NonEmptyStringSchema,
                })
                .strict(),
        })
        .strict(),
]);

const createLegalDocumentAssignmentRequestBaseSchema = z.object({
    legal_document_uuid: LegalDocumentUuidSchema,
    in_force_start: z.string().datetime(),
    in_force_end: z.string().datetime().nullable().optional(),
});

export const CreateLegalDocumentAssignmentRequestSchema: z.ZodType<CreateLegalDocumentAssignmentRequest> =
    z
        .discriminatedUnion('scope', [
            createLegalDocumentAssignmentRequestBaseSchema
                .extend({
                    scope: z.literal(LegalDocumentAssignmentScope.DEFAULT),
                    supersedes_default: z.literal(false),
                })
                .strict(),
            createLegalDocumentAssignmentRequestBaseSchema
                .extend({
                    scope: z.literal(LegalDocumentAssignmentScope.ORGANIZATION),
                    organization_uuid: z.string().uuid(),
                    supersedes_default: z.boolean(),
                })
                .strict(),
        ])
        .superRefine((assignment, context) => {
            if (
                assignment.in_force_end !== undefined &&
                assignment.in_force_end !== null &&
                Date.parse(assignment.in_force_start) >= Date.parse(assignment.in_force_end)
            ) {
                context.addIssue({
                    code: 'custom',
                    path: ['in_force_end'],
                    message: 'in_force_end must be after in_force_start',
                });
            }
        });

const searchLegalDocumentAssignmentsPageResponseSchemaObject = z
    .object({
        legal_document_assignments: z.array(LegalDocumentAssignmentPublicSchema),
        pagination: OpaqueCursorPaginationSchema,
        display_timezone: SystemDisplayTimeZoneSchema,
    })
    .strict();
export const SearchLegalDocumentAssignmentsPageResponseSchema: z.ZodType<SearchLegalDocumentAssignmentsPageResponse> =
    searchLegalDocumentAssignmentsPageResponseSchemaObject;

const ApplicableUserAgreementSchema = z
    .object({
        legal_document_assignment: LegalDocumentAssignmentPublicSchema,
        accepted: z.boolean(),
    })
    .strict();

export const ApplicableTermsResultSchema: z.ZodType<ApplicableTermsResult> = z.discriminatedUnion(
    'coverage_state',
    [
        z
            .object({
                coverage_state: z.literal('exempt'),
                role_assignment_uuid: z.string().uuid(),
            })
            .strict(),
        z
            .object({
                coverage_state: z.literal('not_configured'),
                role_assignment_uuid: z.string().uuid(),
            })
            .strict(),
        z
            .object({
                coverage_state: z.literal('covered'),
                role_assignment_uuid: z.string().uuid(),
                user_agreements: z.array(ApplicableUserAgreementSchema).min(1),
            })
            .strict(),
        z
            .object({
                coverage_state: z.literal('acceptance_required'),
                role_assignment_uuid: z.string().uuid(),
                user_agreements: z.array(ApplicableUserAgreementSchema).min(1),
            })
            .strict(),
    ]
);

export const AcceptApplicableTermsRequestSchema: z.ZodType<AcceptApplicableTermsRequest> = z
    .object({
        role_assignment_uuid: z.string().uuid(),
        legal_document_assignment_uuid: z.string().uuid(),
        legal_document_uuid: z.string().uuid(),
    })
    .strict();

const organizationAgreementStatusBaseSchema = z.object({
    legal_document_assignment_uuid: z.string().uuid(),
    organization: z
        .object({
            organization_uuid: z.string().uuid(),
            organization_name: NonEmptyStringSchema,
        })
        .strict(),
    legal_document: LegalDocumentListItemSchema,
    latest_valid_acceptance: z
        .object({
            organization_legal_document_acceptance_uuid: z.string().uuid(),
            signer_authority_confirmed: z.boolean(),
            accepted_ts: z.string().datetime(),
            accepted_by_user_uuid: z.string().uuid(),
            accepted_by_role_assignment_uuid: z.string().uuid(),
        })
        .strict()
        .nullable(),
});

export const OrganizationAgreementStatusSchema: z.ZodType<OrganizationAgreementStatus> =
    z.discriminatedUnion('validity_kind', [
        organizationAgreementStatusBaseSchema
            .extend({ validity_kind: z.literal('indefinite') })
            .strict(),
        organizationAgreementStatusBaseSchema
            .extend({
                validity_kind: z.literal('finite'),
                acceptance_validity_duration_days: z.number().int().positive(),
            })
            .strict(),
    ]);

export const SearchOrganizationAgreementsPageResponseSchema: z.ZodType<SearchOrganizationAgreementsPageResponse> =
    z
        .object({
            organization_agreements: z.array(OrganizationAgreementStatusSchema),
            pagination: OpaqueCursorPaginationSchema,
            display_timezone: SystemDisplayTimeZoneSchema,
        })
        .strict();

export const AcceptOrganizationAgreementRequestSchema: z.ZodType<AcceptOrganizationAgreementRequest> =
    z
        .object({
            legal_document_assignment_uuid: z.string().uuid(),
            signer_authority_confirmed: z.literal(true),
        })
        .strict();

const createLegalDocumentResponseSchemaObject = z.object({
    legal_document_uuid: LegalDocumentUuidSchema,
});
export const CreateLegalDocumentResponseSchema: typeof createLegalDocumentResponseSchemaObject =
    createLegalDocumentResponseSchemaObject;

const getLegalDocumentResponseSchemaObject = z.object({
    legal_document: LegalDocumentListItemSchema.extend({
        legal_document_text: NonEmptyStringSchema,
    }),
});
export const GetLegalDocumentResponseSchema: typeof getLegalDocumentResponseSchemaObject =
    getLegalDocumentResponseSchemaObject;

const listPendingRequiredLegalDocumentsResponseSchemaObject = z.object({
    legal_documents: z.array(LegalDocumentListItemSchema),
});
export const ListPendingRequiredLegalDocumentsResponseSchema: typeof listPendingRequiredLegalDocumentsResponseSchemaObject =
    listPendingRequiredLegalDocumentsResponseSchemaObject;

const acceptLegalDocumentRequestSchemaObject = z.object({
    accepted_role_assignment_uuid: z.string().uuid().optional(),
});
export const AcceptLegalDocumentRequestSchema: typeof acceptLegalDocumentRequestSchemaObject =
    acceptLegalDocumentRequestSchemaObject;

export const UpdateLegalDocumentRequestSchema: z.ZodType<UpdateLegalDocumentRequest> = z
    .object({
        legal_document_title: NonEmptyStringSchema,
        legal_document_version: NonEmptyStringSchema,
        legal_document_text: NonEmptyStringSchema,
        legal_document_content_type: NonEmptyStringSchema,
        in_force_start: z.string().datetime(),
        in_force_end: z.string().datetime().nullable(),
        source_filename: z.string().optional(),
    })
    .strict()
    .superRefine((legalDocument, context) => {
        if (
            legalDocument.in_force_end !== null &&
            Date.parse(legalDocument.in_force_start) >= Date.parse(legalDocument.in_force_end)
        ) {
            context.addIssue({
                code: 'custom',
                path: ['in_force_end'],
                message: 'in_force_end must be after in_force_start',
            });
        }
    });

export const RevokeLegalDocumentAssignmentRequestSchema: z.ZodType<RevokeLegalDocumentAssignmentRequest> =
    z
        .object({
            revoke_reason: z.string().trim().min(1).max(1000).optional(),
            replacement_legal_document_uuid: z.string().uuid().optional(),
        })
        .strict();

export const RevokeAllLegalDocumentAssignmentsRequestSchema: z.ZodType<RevokeAllLegalDocumentAssignmentsRequest> =
    z.object({ revoke_reason: z.string().trim().min(1).max(1000).optional() }).strict();

export const RevokeAllLegalDocumentAssignmentsResponseSchema: z.ZodType<RevokeAllLegalDocumentAssignmentsResponse> =
    z.object({ revoked_assignment_count: z.number().int().nonnegative() }).strict();

const legalDocumentAcceptanceUserSchema = z
    .object({ user_uuid: z.string().uuid(), primary_email: NonEmptyStringSchema })
    .strict();
const legalDocumentAcceptanceOrganizationSchema = z
    .object({ organization_uuid: z.string().uuid(), organization_name: NonEmptyStringSchema })
    .strict();

export const LegalDocumentAcceptanceWithdrawalSchema: z.ZodType<LegalDocumentAcceptanceWithdrawal> =
    z
        .object({
            withdrawn_ts: z.string().datetime(),
            withdrawn_by: legalDocumentAcceptanceUserSchema,
            withdrawn_by_role_assignment_uuid: z.string().uuid(),
        })
        .strict();

export const LegalDocumentAcceptanceListItemSchema: z.ZodType<LegalDocumentAcceptanceListItem> =
    z.discriminatedUnion('acceptance_kind', [
        z
            .object({
                acceptance_kind: z.literal('user'),
                acceptance_uuid: z.string().uuid(),
                accepted_ts: z.string().datetime(),
                user: legalDocumentAcceptanceUserSchema,
                organization: legalDocumentAcceptanceOrganizationSchema.nullable(),
                accepted_text_matches_current: z.boolean(),
                withdrawal: LegalDocumentAcceptanceWithdrawalSchema.nullable(),
            })
            .strict(),
        z
            .object({
                acceptance_kind: z.literal('organization'),
                acceptance_uuid: z.string().uuid(),
                accepted_ts: z.string().datetime(),
                user: legalDocumentAcceptanceUserSchema,
                organization: legalDocumentAcceptanceOrganizationSchema,
                signer_authority_confirmed: z.boolean(),
                accepted_text_matches_current: z.boolean(),
                withdrawal: LegalDocumentAcceptanceWithdrawalSchema.nullable(),
            })
            .strict(),
    ]);

export const SearchLegalDocumentAcceptancesPageResponseSchema: z.ZodType<SearchLegalDocumentAcceptancesPageResponse> =
    z
        .object({
            acceptances: z.array(LegalDocumentAcceptanceListItemSchema),
            pagination: OpaqueCursorPaginationSchema,
            display_timezone: SystemDisplayTimeZoneSchema,
        })
        .strict();

export const GetLegalDocumentAcceptanceResponseSchema: z.ZodType<GetLegalDocumentAcceptanceResponse> =
    z
        .object({
            acceptance: LegalDocumentAcceptanceListItemSchema,
            legal_document: LegalDocumentListItemSchema,
            accepted_content: z
                .object({
                    legal_document_text: z.string(),
                    legal_document_content_type: NonEmptyStringSchema,
                    content_sha256: NonEmptyStringSchema,
                })
                .strict(),
        })
        .strict();

export const LegalDocumentMutableStatusSchema: z.ZodType<
    UpdateLegalDocumentStatusRequest['status']
> = z.union([z.literal(Status.ACTIVE), z.literal(Status.DISABLED)]);

const updateLegalDocumentStatusRequestSchemaObject = z.object({
    status: LegalDocumentMutableStatusSchema,
});
export const UpdateLegalDocumentStatusRequestSchema: typeof updateLegalDocumentStatusRequestSchemaObject =
    updateLegalDocumentStatusRequestSchemaObject;
